import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '@/utils/logger';
import { platformSettingsApi } from '../lib/api';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchTheme();
  }, []);
  
  const fetchTheme = async () => {
    try {
      const response = await platformSettingsApi.get();
      const themeData = response.data;
      setTheme(themeData);
      applyTheme(themeData);
      setLoading(false);
    } catch (error) {
      logger.error('Failed to fetch theme:', error);
      // Use default theme
      const defaultTheme = {
        platform_name: 'Genera System',
        primary_color: '#a855f7',
        secondary_color: '#8b5cf6',
        accent_color: '#c084fc',
        logo_url: null,
        favicon_url: null,
        login_background: null
      };
      setTheme(defaultTheme);
      applyTheme(defaultTheme);
      setLoading(false);
    }
  };
  
  const applyTheme = (themeData) => {
    if (!themeData) return;
    
    // Apply CSS variables
    document.documentElement.style.setProperty('--primary-color', themeData.primary_color);
    document.documentElement.style.setProperty('--secondary-color', themeData.secondary_color);
    document.documentElement.style.setProperty('--accent-color', themeData.accent_color);
    
    // Update page title
    if (themeData.platform_name) {
      document.title = themeData.platform_name;
    }
    
    // Update favicon
    if (themeData.favicon_url) {
      updateFavicon(themeData.favicon_url);
    }
  };
  
  const updateFavicon = (faviconUrl) => {
    // Update all favicon links
    const links = document.querySelectorAll('link[rel*="icon"]');
    links.forEach(link => {
      link.href = faviconUrl;
    });
    
    // If no favicon link exists, create one
    if (links.length === 0) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    }
  };
  
  return (
    <ThemeContext.Provider value={{ theme, refreshTheme: fetchTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeContext;
